/**
 * Curriculum Manager - Controls the learning progression through lessons.
 *
 * This module defines types for lessons, content, and completion criteria,
 * plus functions for navigating and tracking progress through the curriculum.
 */

import type { Market, MarketState, Position, Order, OrderBook, Trade } from '../engine/types'

// =============================================================================
// Content Types
// =============================================================================

/**
 * Text content with optional visualization.
 */
export interface TextContent {
  /** Markdown content to render */
  markdown: string
  /** Optional visualization to display alongside (e.g., 'order-book', 'probability') */
  visualizationId?: string
}

/**
 * Interactive simulation content where users must complete a task.
 */
export interface InteractiveContent {
  /** The scenario to load for this interactive session */
  scenario: SimulationScenario
  /** Instructions explaining what the user should do */
  instructions: string
  /** Progressive hints to help if user gets stuck */
  hints: string[]
  /** Function to check if user has completed the task */
  successCondition: (state: SimulationState) => boolean
}

/**
 * Quiz question with multiple choice options.
 */
export interface QuizContent {
  /** The question to ask */
  question: string
  /** Available options to choose from */
  options: QuizOption[]
  /** ID of the correct answer */
  correctOptionId: string
  /** Explanation shown after answering (whether correct or not) */
  explanation: string
}

/**
 * A single quiz option.
 */
export interface QuizOption {
  /** Unique identifier for this option */
  id: string
  /** Display text for the option */
  text: string
}

/**
 * Sandbox content for free exploration with selected tools.
 */
export interface SandboxContent {
  /** The scenario to load for sandbox mode */
  scenario: SimulationScenario
  /** Which trading tools are available in this sandbox */
  tools: SandboxTool[]
}

/**
 * Available tools in sandbox mode.
 */
export type SandboxTool = 'limit-order' | 'market-order' | 'speed-control' | 'cancel-order'

// =============================================================================
// Lesson Content Union Type
// =============================================================================

/**
 * A single piece of lesson content.
 * Uses discriminated union for type-safe handling.
 */
export type LessonContent =
  | { type: 'text'; data: TextContent }
  | { type: 'interactive'; data: InteractiveContent }
  | { type: 'quiz'; data: QuizContent }
  | { type: 'sandbox'; data: SandboxContent }

// =============================================================================
// Completion Criteria
// =============================================================================

/**
 * How to determine if a lesson is complete.
 */
export type CompletionCriteriaType = 'all-content' | 'quiz-pass' | 'interactive-complete'

/**
 * Criteria for marking a lesson as complete.
 */
export interface CompletionCriteria {
  /** Type of completion check */
  type: CompletionCriteriaType
  /** For quiz-pass type, minimum score required (0-100) */
  minQuizScore?: number
}

// =============================================================================
// Simulation Scenario Types
// =============================================================================

/**
 * A pre-configured market scenario for lessons.
 */
export interface SimulationScenario {
  /** Unique identifier */
  id: string
  /** Human-readable name */
  name: string
  /** Description of the scenario */
  description: string
  /** The market configuration */
  market: Market
  /** Pre-populated order book state */
  initialOrderBook: OrderBook
  /** User's starting balance for this scenario */
  userStartingBalance: number
}

/**
 * Create a deep clone of an order book so lesson and sandbox scenarios stay immutable.
 */
export function cloneOrderBook(orderBook: OrderBook): OrderBook {
  return {
    bids: orderBook.bids.map((order) => ({ ...order })),
    asks: orderBook.asks.map((order) => ({ ...order })),
    trades: orderBook.trades.map((trade: Trade) => ({ ...trade })),
    lastPrice: orderBook.lastPrice,
  }
}

/**
 * Instantiate a fresh live market from a scenario definition.
 */
export function instantiateScenario(scenario: SimulationScenario): {
  market: Market
  balance: number
} {
  return {
    market: {
      ...scenario.market,
      orderBook: cloneOrderBook(scenario.initialOrderBook),
      resolved: false,
      resolution: null,
    },
    balance: scenario.userStartingBalance,
  }
}

/**
 * Current state of a simulation (for checking success conditions).
 */
export interface SimulationState {
  /** Current market state */
  market: Market
  /** Derived market metrics */
  marketState: MarketState
  /** User's current balance */
  userBalance: number
  /** User's current positions */
  userPositions: Position[]
  /** Current simulation time (ms since start) */
  simulationTime: number
  /** All orders placed by the user */
  orderHistory: Order[]
}

// =============================================================================
// Lesson Type
// =============================================================================

/**
 * A single lesson in the curriculum.
 */
export interface Lesson {
  /** Unique identifier (e.g., 'lesson-1', 'intro-to-markets') */
  id: string
  /** Display title */
  title: string
  /** Brief description of what the lesson covers */
  description: string
  /** Order in the curriculum (1-based) */
  order: number
  /** Array of content blocks that make up the lesson */
  content: LessonContent[]
  /** Learning objectives (displayed to user) */
  objectives: string[]
  /** IDs of lessons that must be completed first */
  prerequisites: string[]
  /** How to determine if this lesson is complete */
  completionCriteria: CompletionCriteria
}

// =============================================================================
// Lesson Registry (will be populated by lesson files)
// =============================================================================

/**
 * Registry of all lessons, keyed by ID.
 * Lessons will be registered here from separate lesson files.
 */
const lessonRegistry: Map<string, Lesson> = new Map()

/**
 * Register a lesson in the curriculum.
 * Called by lesson definition files.
 */
export function registerLesson(lesson: Lesson): void {
  lessonRegistry.set(lesson.id, lesson)
}

/**
 * Register multiple lessons at once.
 */
export function registerLessons(lessons: Lesson[]): void {
  for (const lesson of lessons) {
    registerLesson(lesson)
  }
}

// =============================================================================
// Curriculum Manager - Lesson Access
// =============================================================================

/**
 * Get a lesson by its ID.
 * @returns The lesson, or undefined if not found
 */
export function getLesson(id: string): Lesson | undefined {
  return lessonRegistry.get(id)
}

/**
 * Get all lessons, sorted by order.
 * @returns Array of lessons sorted by their order property
 */
export function getAllLessons(): Lesson[] {
  return Array.from(lessonRegistry.values()).sort((a, b) => a.order - b.order)
}

/**
 * Check if a lesson is unlocked based on completed prerequisites.
 * @param lessonId - The lesson to check
 * @param completedLessons - Set of completed lesson IDs
 * @returns true if all prerequisites are met
 */
export function isLessonUnlocked(
  lessonId: string,
  completedLessons: Set<string>
): boolean {
  const lesson = getLesson(lessonId)
  if (!lesson) {
    return false
  }

  // No prerequisites means always unlocked
  if (lesson.prerequisites.length === 0) {
    return true
  }

  // Check all prerequisites are completed
  return lesson.prerequisites.every((prereqId) => completedLessons.has(prereqId))
}

// =============================================================================
// Curriculum Manager - Progression Logic
// =============================================================================

/**
 * Get the next lesson the user should work on.
 * Finds the first unlocked lesson that isn't completed yet.
 * @param completedLessons - Set of completed lesson IDs
 * @returns The next lesson to do, or undefined if all completed
 */
export function getNextLesson(completedLessons: Set<string>): Lesson | undefined {
  const allLessons = getAllLessons()

  for (const lesson of allLessons) {
    // Skip completed lessons
    if (completedLessons.has(lesson.id)) {
      continue
    }

    // Check if prerequisites are met
    if (isLessonUnlocked(lesson.id, completedLessons)) {
      return lesson
    }
  }

  return undefined
}

/**
 * State tracked during a lesson for completion checking.
 */
export interface LessonCompletionState {
  /** Number of content blocks viewed/completed */
  contentProgress: number
  /** Total content blocks in the lesson */
  totalContent: number
  /** Quiz scores by content index */
  quizScores: Record<number, number>
  /** Whether interactive sections were completed */
  interactiveCompleted: Record<number, boolean>
}

/**
 * Check if a lesson's completion criteria are met.
 * @param lesson - The lesson to check
 * @param state - Current completion state
 * @returns true if lesson is complete
 */
export function checkCompletion(
  lesson: Lesson,
  state: LessonCompletionState
): boolean {
  const { type, minQuizScore } = lesson.completionCriteria

  switch (type) {
    case 'all-content':
      // All content must be viewed
      return state.contentProgress >= state.totalContent

    case 'quiz-pass': {
      // All quizzes must be passed with minimum score
      const requiredScore = minQuizScore ?? 70
      const quizIndices = lesson.content
        .map((c, i) => (c.type === 'quiz' ? i : -1))
        .filter((i) => i >= 0)

      if (quizIndices.length === 0) {
        // No quizzes, fall back to all-content
        return state.contentProgress >= state.totalContent
      }

      return quizIndices.every((idx) => {
        const score = state.quizScores[idx]
        return score !== undefined && score >= requiredScore
      })
    }

    case 'interactive-complete': {
      // All interactive sections must be completed
      const interactiveIndices = lesson.content
        .map((c, i) => (c.type === 'interactive' ? i : -1))
        .filter((i) => i >= 0)

      if (interactiveIndices.length === 0) {
        // No interactive content, fall back to all-content
        return state.contentProgress >= state.totalContent
      }

      return interactiveIndices.every((idx) => state.interactiveCompleted[idx] === true)
    }

    default:
      return false
  }
}

/**
 * Calculate overall curriculum progress.
 * @param completedLessons - Set of completed lesson IDs
 * @returns Progress percentage (0-100)
 */
export function getLessonProgress(completedLessons: Set<string>): number {
  const allLessons = getAllLessons()

  if (allLessons.length === 0) {
    return 0
  }

  const completed = allLessons.filter((l) => completedLessons.has(l.id)).length
  return Math.round((completed / allLessons.length) * 100)
}

/**
 * Get a summary of curriculum progress.
 */
export interface CurriculumProgress {
  /** Number of completed lessons */
  completed: number
  /** Total number of lessons */
  total: number
  /** Percentage complete (0-100) */
  percentage: number
  /** The next lesson to work on, if any */
  nextLesson: Lesson | undefined
}

/**
 * Get detailed curriculum progress information.
 * @param completedLessons - Set of completed lesson IDs
 */
export function getCurriculumProgress(completedLessons: Set<string>): CurriculumProgress {
  const allLessons = getAllLessons()
  const completed = allLessons.filter((l) => completedLessons.has(l.id)).length

  return {
    completed,
    total: allLessons.length,
    percentage: allLessons.length > 0 ? Math.round((completed / allLessons.length) * 100) : 0,
    nextLesson: getNextLesson(completedLessons),
  }
}

// =============================================================================
// Export type for lesson registration in other files
// =============================================================================

export type { Lesson as LessonType }
